import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FormRow } from "../components";
import ParagraphList from "../components/ParagraphList";
import AlertDialog from "../components/AlertDialog";
import { addOwnBook, getAllOwnBooks } from "../controller/addBook/premium/ownBooksController";

/* ─── MIN LENGTH RULE ─────────────────────────────────── */
const MIN_CHARS = 200;
const INFRINGEMENT_CONSENT_KEY = "readrivals.infringementConsent.v1";

/* ─── styled layout ───────────────────────────────────── */
const Wrapper = styled.div`
  width: 100%;

  h3 {
    margin-top: 0;
    margin-bottom: 1.5rem;
    font-size: 1.2rem;
    color: var(--grey-800, #1f2937);
  }

  .fields-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .columns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    align-items: start;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .para-panel {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .para-panel label {
    font-weight: 600;
    font-size: 0.85rem;
    color: var(--grey-700, #374151);
    text-transform: capitalize;
    letter-spacing: 0.03em;
  }

  .para-textarea {
    width: 100%;
    min-height: 220px;
    resize: vertical;
    border: 1px solid var(--grey-300, #d1d5db);
    border-radius: var(--borderRadius, 6px);
    padding: 0.75rem;
    font-size: 0.9rem;
    font-family: inherit;
    color: var(--grey-800, #1f2937);
    background: var(--white, #fff);
    transition: border-color 0.15s;
    line-height: 1.55;

    &:focus {
      outline: none;
      border-color: var(--primary-500, #2563eb);
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
    }
  }

  .para-textarea.error {
    border-color: var(--red-dark, #dc2626);
  }

  .char-counter {
    font-size: 0.72rem;
    color: var(--grey-400, #9ca3af);
    text-align: right;
    margin-top: -0.4rem;

    &.warn { color: var(--red-dark, #dc2626); }
    &.ok   { color: #16a34a; }
  }

  .add-para-btn {
    align-self: flex-start;
    background: var(--primary-500, #2563eb);
    color: #fff;
    border: none;
    border-radius: var(--borderRadius, 6px);
    padding: 0.5rem 1.4rem;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, transform 0.1s;

    &:hover  { background: var(--primary-700, #1d4ed8); }
    &:active { transform: scale(0.97); }
  }

  .list-panel {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .list-panel label {
    font-weight: 600;
    font-size: 0.85rem;
    color: var(--grey-700, #374151);
    text-transform: capitalize;
    letter-spacing: 0.03em;
  }

  .list-scroll {
    max-height: 340px;
    overflow-y: auto;
    padding-right: 4px;
  }

  .submit-row {
    margin-top: 2rem;
    display: flex;
    gap: 1rem;
    justify-content: flex-end;

    @media (max-width: 480px) {
      flex-direction: column;
    }
  }

  .btn-submit {
    background: var(--primary-500, #2563eb);
    color: #fff;
    border: none;
    border-radius: var(--borderRadius, 6px);
    padding: 0.6rem 2rem;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;

    &:hover    { background: var(--primary-700, #1d4ed8); }
    &:disabled { background: var(--grey-300, #d1d5db); cursor: not-allowed; }
  }

  .btn-clear {
    background: var(--grey-200, #e5e7eb);
    color: var(--grey-700, #374151);
    border: none;
    border-radius: var(--borderRadius, 6px);
    padding: 0.6rem 1.6rem;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;

    &:hover { background: var(--grey-400, #9ca3af); color: #fff; }
  }
`;

/* ─── component ───────────────────────────────────────── */
const ScratchBookForm = ({ onSubmitBook }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading } = useSelector((store) => store.ownBooks);

  const [title, setTitle]         = useState("");
  const [author, setAuthor]       = useState("");
  const [imageUrl, setImageUrl]   = useState("");
  const [paraInput, setParaInput] = useState("");
  const [paragraphs, setParagraphs] = useState([]);
  const [paraError, setParaError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  useEffect(() => {
    try {
      const existingConsent = localStorage.getItem(INFRINGEMENT_CONSENT_KEY);
      if (!existingConsent) {
        setShowConsentDialog(true);
      }
    } catch (_error) {
      // If storage is unavailable, still require session-time consent.
      setShowConsentDialog(true);
    }
  }, []);

  /* char counter colour */
  const charCount = paraInput.length;
  const counterClass =
    charCount === 0
      ? ""
      : charCount < MIN_CHARS
      ? "warn"
      : "ok";

  /* ── add paragraph ── */
  const handleAddParagraph = () => {
    const trimmed = paraInput.trim();

    if (!trimmed) {
      setParaError("Paragraph cannot be empty.");
      return;
    }
    if (trimmed.length < MIN_CHARS) {
      setParaError(
        `Paragraph is too short (${trimmed.length} chars). Minimum is ${MIN_CHARS} characters.`
      );
      return;
    }

    setParaError("");
    setParagraphs((prev) => [...prev, { paragraph: trimmed }]);
    setParaInput("");
  };

  /* ── delete paragraph ── */
  const handleDelete = (idx) => {
    setParagraphs((prev) => prev.filter((_, i) => i !== idx));
  };

  /* ── reorder paragraphs ── */
  const handleReorder = (updated) => {
    setParagraphs(updated);
  };

  /* ── validate before submit ── */
  const validate = () => {
    if (!title.trim()) {
      toast.error("Title is required.");
      return false;
    }
    if (!author.trim()) {
      toast.error("Author name is required.");
      return false;
    }
    if (paragraphs.length === 0) {
      toast.error("Please add at least one paragraph before submitting.");
      return false;
    }
    return true;
  };

  const handleSubmitClick = () => {
    if (validate()) setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    const payload = {
      title: title.trim(),
      author: author.trim(),
      image: imageUrl.trim() || null,
      level: "A",
      paragraphs,
    };

    try {
      if (onSubmitBook) {
        await Promise.resolve(onSubmitBook(payload));
      } else {
        await dispatch(addOwnBook(payload)).unwrap();
        await dispatch(getAllOwnBooks()).unwrap();
        navigate("/own-books");
      }
      setConfirmOpen(false);
      handleClear();
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Failed to add own book");
    }
  };

  const handleClear = () => {
    setTitle("");
    setAuthor("");
    setImageUrl("");
    setParaInput("");
    setParagraphs([]);
    setParaError("");
  };

  const handleConsentAgree = () => {
    if (!consentChecked) return;
    try {
      localStorage.setItem(
        INFRINGEMENT_CONSENT_KEY,
        JSON.stringify({ accepted: true, acceptedAt: new Date().toISOString() })
      );
    } catch (_error) {
      // Ignore storage errors and continue with session consent.
    }
    setShowConsentDialog(false);
  };

  const handleConsentDecline = () => {
    setShowConsentDialog(false);
    navigate("/own-books");
  };

  return (
    <Wrapper>
      <h3>Curate From Scratch</h3>

      {/* ── top fields ── */}
      <div className="fields-row">
        <FormRow
          type="text"
          name="Title"
          placeholder="Book title"
          required
          value={title}
          handleChange={(e) => setTitle(e.target.value)}
        />
        <FormRow
          type="text"
          name="Author"
          placeholder="Author name"
          required
          value={author}
          handleChange={(e) => setAuthor(e.target.value)}
        />
        <FormRow
          type="url"
          name="Image URL"
          placeholder="https://… (optional)"
          value={imageUrl}
          handleChange={(e) => setImageUrl(e.target.value)}
        />
      </div>

      {/* ── two-column body ── */}
      <div className="columns">
        {/* left: paragraph input */}
        <div className="para-panel">
          <label>Paragraph</label>
          <textarea
            className={`para-textarea${paraError ? " error" : ""}`}
            placeholder={`Write or paste a paragraph here (min ${MIN_CHARS} characters)…`}
            value={paraInput}
            onChange={(e) => {
              setParaInput(e.target.value);
              if (paraError) setParaError("");
            }}
          />
          <span className={`char-counter ${counterClass}`}>
            {charCount} / {MIN_CHARS} min chars
          </span>
          {paraError && (
            <span style={{ fontSize: "0.78rem", color: "var(--red-dark, #dc2626)" }}>
              {paraError}
            </span>
          )}
          <button className="add-para-btn" onClick={handleAddParagraph}>
            + Add Paragraph
          </button>
        </div>

        {/* right: animated list */}
        <div className="list-panel">
          <label>
            Paragraphs ({paragraphs.length})
            {paragraphs.length > 1 && (
              <span
                style={{ fontWeight: 400, color: "var(--grey-400)", marginLeft: "0.4rem", fontSize: "0.72rem" }}
              >
                — drag to reorder · swipe right to delete
              </span>
            )}
          </label>
          <div className="list-scroll">
            <ParagraphList
              paragraphs={paragraphs}
              onDelete={handleDelete}
              onReorder={handleReorder}
            />
          </div>
        </div>
      </div>

      {/* ── action row ── */}
      <div className="submit-row">
        <button className="btn-clear" onClick={handleClear}>
          Clear
        </button>
        <button
          className="btn-submit"
          onClick={handleSubmitClick}
          disabled={isLoading}
        >
          {isLoading ? "Please Wait…" : "Submit Book"}
        </button>
      </div>

      <AlertDialog
        open={showConsentDialog}
        onClose={handleConsentDecline}
        title="Copyright & Infringement Acknowledgment"
        description={
          <div style={{ maxWidth: "560px" }}>
            <p style={{ marginTop: 0 }}>
              Before creating a scratch book, you must confirm that you will only upload
              original content or content you are authorized to use.
            </p>
            <ul style={{ paddingLeft: "1.2rem" }}>
              <li>Do not upload copyrighted material without permission.</li>
              <li>
                Accounts that receive repeated valid DMCA notices may be permanently
                terminated.
              </li>
              <li>
                You are responsible for claims and costs resulting from unauthorized use
                of third-party copyrighted content.
              </li>
            </ul>
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "0.5rem",
                marginTop: "0.75rem",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                style={{ marginTop: "4px" }}
              />
              <span style={{ fontSize: "0.9rem" }}>
                I acknowledge and agree to the ReadRivals copyright and infringement
                policy.
              </span>
            </label>
          </div>
        }
        agreeText="I Agree"
        disagreeText="Cancel"
        runTaskAndClose={handleConsentAgree}
        agreeDisabled={!consentChecked}
      />

      <AlertDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        runTaskAndClose={handleConfirm}
        title="Ready to submit?"
        description="This book will be saved to the server."
        agreeText="Submit"
        disagreeText="Not Yet!"
      />
    </Wrapper>
  );
};

export default ScratchBookForm;
